// Right-sized remote cover art URLs.
//
// The catalog stores one large variant per cover (Apple lookups are upsized
// to 1024x1024 server-side to match mixes.art_cover), but list tiles render
// at 24-130 CSS px — fetching the stored URL ships 10-30x more bytes than
// the tile needs, which is exactly the "covers lag behind the text" effect
// on slow connections. Both major art CDNs encode the size in the URL, so
// the client can ask for what it will actually render:
//
//   Apple (…mzstatic.com): trailing `/{w}x{h}bb.jpg` segment — the CDN
//   accepts arbitrary sizes, and `bb.jpg` names the OUTPUT format (it
//   transcodes .png sources), the same contract mixArtwork.js relies on
//   server-side.
//   SoundCloud (…sndcdn.com): fixed suffix ladder (-large = 100px,
//   -t200x200, -t500x500, -original).
//
// ONE variant for every UI tile: cssPx ≤ TILE_MAX_CSS always maps to the
// 320px file, deliberately ignoring devicePixelRatio. Per-surface (and
// per-DPR) variants fragmented the HTTP cache — the artist hero, the list
// avatars, and the supporting-artist bubbles each cold-fetched their own
// URL for the SAME cover, so navigation stopped painting from cache and
// "every cover lags" (the pre-rewrite app used one stored URL everywhere,
// which is why it felt instant once warm). 320 renders crisp up to
// 160css × 2, downscales fine in tiny slots, and is still 6-10× lighter
// than the originals. A cover downloads ONCE and is instant on every
// surface after — worth far more than a per-slot byte-perfect size.
//
// Unknown hosts and non-absolute URLs (local paths, data:) pass through
// unchanged, and <CoverArt> falls back to the original URL if a rewritten
// variant ever 404s.

import { isFirstPartyUrlBlocked } from './no1001';

const TILE = 320;
const TILE_MAX_CSS = 160; // every converted call site today is ≤130css

// Larger-than-tile call sites (none in the renderer today, but heroes may
// convert later): modest ladder, device pixels, DPR capped at 2 — on 3×
// phones an uncapped DPR promoted sizes to near-original files, erasing
// the byte win exactly where bandwidth is scarcest.
const BIG_BUCKETS = [640, 1024];

export const coverBucket = (cssPx) => {
    if (cssPx <= TILE_MAX_CSS) return TILE;
    const dpr = Math.min((typeof window !== 'undefined' && window.devicePixelRatio) || 1, 2);
    const target = Math.ceil(cssPx * dpr);
    for (const b of BIG_BUCKETS) {
        if (b * 1.25 >= target) return b; // ≤1.25× upscale is invisible; prefer the shared smaller file
    }
    return BIG_BUCKETS[BIG_BUCKETS.length - 1];
};

const APPLE_SIZE_RE = /\/\d+x\d+[a-z]{0,2}(-\d+)?\.(jpg|jpeg|png|webp)(\?.*)?$/i;
const SC_SUFFIX_RE = /-(badge|mini|tiny|small|large|original|crop|t\d+x\d+)\.(jpg|jpeg|png|webp)(\?.*)?$/i;

// SoundCloud's ladder has nothing between 200 and 500px, so t500x500 is
// the one variant that covers the 320 tile AND the big buckets — every
// SoundCloud cover is one URL app-wide. Never -original: that's the raw
// upload (can be a multi-MB PNG).
const SC_VARIANT = 't500x500';

// YouTube: `hqdefault` (480x360) is a 4:3 frame with the 16:9 picture
// letterboxed inside it — black bars baked into the file, which no
// object-fit can crop. `mqdefault` (320x180) and `hq720` (1280x720) are
// the bare 16:9 picture. hq720 is missing for older uploads; <CoverArt>
// falls back to the stored URL when a variant 404s.
const YT_THUMB_RE = /\/vi\/([^/]+)\/(hqdefault|sddefault|default|0)\.jpg(\?.*)?$/i;

export const coverUrl = (url, cssPx) => {
    // Never a 1001tracklists asset, whatever the caller passes (see no1001.js).
    if (isFirstPartyUrlBlocked(url)) return null;
    if (!url || typeof url !== 'string' || !cssPx) return url || null;
    let host;
    try {
        host = new URL(url).hostname;
    } catch {
        return url; // relative/local/data: URL — nothing to rewrite
    }
    const b = coverBucket(cssPx);
    if (host.endsWith('.mzstatic.com') && APPLE_SIZE_RE.test(url)) {
        return url.replace(APPLE_SIZE_RE, `/${b}x${b}bb.jpg`);
    }
    if (host.endsWith('.sndcdn.com') && SC_SUFFIX_RE.test(url)) {
        return url.replace(SC_SUFFIX_RE, `-${SC_VARIANT}.jpg`);
    }
    if ((host === 'img.youtube.com' || host.endsWith('.ytimg.com')) && YT_THUMB_RE.test(url)) {
        return url.replace(YT_THUMB_RE, `/vi/$1/${b === TILE ? 'mqdefault' : 'hq720'}.jpg`);
    }
    return url;
};
